import {
	type CanvasMountHandle,
	type ColorRGB,
	getAccent2Rgb,
	getAccentRgb,
	getGreenRgb,
	prefersReducedMotion,
	rgba,
} from "./types";

const MAX_DPR = 2;

export interface GraphFieldOptions {
	count?: number;
	linkDist?: number;
	speed?: number;
	nodeR?: number;
	pulseRate?: number;
	accentRGB?: ColorRGB;
	acc2RGB?: ColorRGB;
	dim?: boolean;
}

export interface GraphFieldHandle extends CanvasMountHandle {
	burst(n?: number): void;
}

interface Node {
	x: number;
	y: number;
	vx: number;
	vy: number;
	r: number;
	hub: boolean;
	phase: number;
}

interface Edge {
	i: number;
	j: number;
	d: number;
}

interface Pulse {
	a: number;
	b: number;
	born: number;
	life: number;
	hue: ColorRGB;
}

export function createGraphField(
	canvas: HTMLCanvasElement,
	opts: GraphFieldOptions = {},
): GraphFieldHandle {
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		return { burst() {}, destroy() {} };
	}
	const reduced = prefersReducedMotion();
	const count = opts.count ?? 26;
	const linkDist = opts.linkDist ?? 150;
	const speed = opts.speed ?? 0.16;
	const baseR = opts.nodeR ?? 2.4;
	const pulseRate = opts.pulseRate ?? 1400;
	const dimNodes = opts.dim ?? false;
	const accent = (): ColorRGB => opts.accentRGB ?? getAccentRgb();
	const accent2 = (): ColorRGB => opts.acc2RGB ?? getAccent2Rgb();

	let width = 0;
	let height = 0;
	let nodes: Node[] = [];
	let edges: Edge[] = [];
	let pulses: Pulse[] = [];
	let rafId: number | null = null;
	let lastPulse = 0;
	let running = true;
	let resizeTimer: ReturnType<typeof setTimeout> | null = null;

	const size = (): void => {
		const r = canvas.getBoundingClientRect();
		width = Math.max(1, r.width);
		height = Math.max(1, r.height);
		const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	};

	const build = (): void => {
		nodes = [];
		for (let i = 0; i < count; i++) {
			nodes.push({
				x: Math.random() * width,
				y: Math.random() * height,
				vx: (Math.random() - 0.5) * speed,
				vy: (Math.random() - 0.5) * speed,
				r: baseR + Math.random() * 1.8,
				hub: Math.random() < 0.18,
				phase: Math.random() * Math.PI * 2,
			});
		}
	};

	const computeEdges = (): void => {
		edges = [];
		for (let i = 0; i < nodes.length; i++) {
			for (let j = i + 1; j < nodes.length; j++) {
				const ni = nodes[i] as (typeof nodes)[number];
				const nj = nodes[j] as (typeof nodes)[number];
				const dx = ni.x - nj.x;
				const dy = ni.y - nj.y;
				const d = Math.hypot(dx, dy);
				if (d < linkDist) edges.push({ i, j, d });
			}
		}
	};

	const spawnPulse = (now: number): void => {
		if (!edges.length) return;
		const idx = Math.floor(Math.random() * edges.length);
		const e = edges[idx] as (typeof edges)[number];
		pulses.push({
			a: e.i,
			b: e.j,
			born: now,
			life: 900 + Math.random() * 500,
			hue: Math.random() < 0.7 ? accent() : accent2(),
		});
	};

	const drawStatic = (): void => {
		computeEdges();
		ctx.clearRect(0, 0, width, height);
		for (const { i, j, d } of edges) {
			const a = nodes[i] as (typeof nodes)[number];
			const b = nodes[j] as (typeof nodes)[number];
			ctx.strokeStyle = rgba(accent(), (1 - d / linkDist) * 0.25);
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(a.x, a.y);
			ctx.lineTo(b.x, b.y);
			ctx.stroke();
		}
		for (const n of nodes) {
			ctx.fillStyle = n.hub ? rgba(accent(), 0.8) : "rgba(200,215,210,0.4)";
			ctx.beginPath();
			ctx.arc(n.x, n.y, n.r * (n.hub ? 1.8 : 1), 0, Math.PI * 2);
			ctx.fill();
		}
	};

	const step = (now: number): void => {
		if (!running) return;
		ctx.clearRect(0, 0, width, height);
		for (const n of nodes) {
			n.x += n.vx;
			n.y += n.vy;
			if (n.x < -20) n.x = width + 20;
			else if (n.x > width + 20) n.x = -20;
			if (n.y < -20) n.y = height + 20;
			else if (n.y > height + 20) n.y = -20;
		}
		computeEdges();

		for (const { i, j, d } of edges) {
			const a = nodes[i] as (typeof nodes)[number];
			const b = nodes[j] as (typeof nodes)[number];
			const alpha = (1 - d / linkDist) * (dimNodes ? 0.22 : 0.32);
			ctx.strokeStyle = rgba(accent(), alpha);
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(a.x, a.y);
			ctx.lineTo(b.x, b.y);
			ctx.stroke();
		}

		pulses = pulses.filter((p) => now - p.born < p.life);
		for (const p of pulses) {
			const a = nodes[p.a];
			const b = nodes[p.b];
			if (!a || !b) continue;
			const k = (now - p.born) / p.life;
			const x = a.x + (b.x - a.x) * k;
			const y = a.y + (b.y - a.y) * k;
			const fade = Math.sin(k * Math.PI);
			const g = ctx.createRadialGradient(x, y, 0, x, y, 9);
			g.addColorStop(0, rgba(p.hue, 0.9 * fade));
			g.addColorStop(1, rgba(p.hue, 0));
			ctx.fillStyle = g;
			ctx.beginPath();
			ctx.arc(x, y, 9, 0, Math.PI * 2);
			ctx.fill();
			ctx.fillStyle = `rgba(255,255,255,${(0.85 * fade).toFixed(3)})`;
			ctx.beginPath();
			ctx.arc(x, y, 1.6, 0, Math.PI * 2);
			ctx.fill();
		}

		for (const n of nodes) {
			const tw = 0.5 + 0.5 * Math.sin(now * 0.001 + n.phase);
			const r = n.r * (n.hub ? 1.8 : 1);
			if (n.hub) {
				const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 5);
				g.addColorStop(0, rgba(accent(), 0.22 * tw + 0.08));
				g.addColorStop(1, rgba(accent(), 0));
				ctx.fillStyle = g;
				ctx.beginPath();
				ctx.arc(n.x, n.y, r * 5, 0, Math.PI * 2);
				ctx.fill();
			}
			ctx.fillStyle = n.hub
				? rgba(accent(), 0.7 + 0.3 * tw)
				: `rgba(200,215,210,${(0.3 + 0.3 * tw).toFixed(3)})`;
			ctx.beginPath();
			ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
			ctx.fill();
		}

		if (now - lastPulse > pulseRate) {
			spawnPulse(now);
			lastPulse = now;
		}
		rafId = requestAnimationFrame(step);
	};

	const onResize = (): void => {
		if (resizeTimer) clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			size();
			build();
		}, 180);
	};

	const io = new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				if (e.isIntersecting) {
					if (!running) {
						running = true;
						if (!reduced) rafId = requestAnimationFrame(step);
					}
				} else {
					running = false;
					if (rafId !== null) {
						cancelAnimationFrame(rafId);
						rafId = null;
					}
				}
			}
		},
		{ threshold: 0 },
	);

	size();
	build();
	if (reduced) {
		drawStatic();
	} else {
		rafId = requestAnimationFrame(step);
	}
	io.observe(canvas);
	window.addEventListener("resize", onResize, { passive: true });

	return {
		burst(n = 6) {
			for (let k = 0; k < n; k++) {
				setTimeout(() => spawnPulse(performance.now()), k * 90);
			}
		},
		destroy() {
			if (rafId !== null) cancelAnimationFrame(rafId);
			if (resizeTimer) clearTimeout(resizeTimer);
			io.disconnect();
			window.removeEventListener("resize", onResize);
		},
	};
}

void getGreenRgb;
